/*
  <size-chart-launcher> — accessibility hardening for the size-chart modal.

  Dawn's <modal-dialog> (global.js) handles open/close, Escape, backdrop click,
  and focus return. But its show() focuses the [role="dialog"] container, which
  does NOT arm Dawn's Tab-wrap handler (that only attaches when focus lands on the
  dialog's first/last focusable). The first Shift+Tab then escapes the modal.

  This component closes that gap WITHOUT editing base theme JS:
    - On open: move focus to the close button (= the first focusable, which arms
      Dawn's trap), and mark every other <body> child `inert` so the background is
      neither focusable nor exposed to assistive tech.
    - On close: remove `inert` synchronously on Dawn's `modalClosed` event, which
      fires inside hide() BEFORE focus is returned to the trigger — so the trigger
      isn't inert at the moment Dawn focuses it.

  `inert` is ignored by older browsers, which degrade to aria-modal + the armed trap.
*/
if (!customElements.get('size-chart-launcher')) {
  customElements.define(
    'size-chart-launcher',
    class SizeChartLauncher extends HTMLElement {
      connectedCallback() {
        this.modal = document.getElementById(this.dataset.modalId);
        if (!this.modal) return;

        this.inerted = [];
        this.onClosed = this.deactivate.bind(this);

        this.observer = new MutationObserver(() => {
          if (this.modal.hasAttribute('open')) this.activate();
        });
        this.observer.observe(this.modal, { attributes: true, attributeFilter: ['open'] });

        document.body.addEventListener('modalClosed', this.onClosed);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        document.body.removeEventListener('modalClosed', this.onClosed);
        this.deactivate();
      }

      activate() {
        if (this.inerted.length) return; // already open

        this.inerted = Array.from(document.body.children).filter(
          (el) => el !== this.modal && el.nodeName !== 'SCRIPT' && !el.hasAttribute('inert')
        );
        this.inerted.forEach((el) => el.setAttribute('inert', ''));

        // Focusing the close button (the dialog's first focusable) arms Dawn's
        // trapFocus keydown handler, closing the Shift+Tab leak.
        const close = this.modal.querySelector('[id^="ModalClose-"]');
        if (close) requestAnimationFrame(() => close.focus());
      }

      deactivate() {
        this.inerted.forEach((el) => el.removeAttribute('inert'));
        this.inerted = [];
      }
    }
  );
}
